import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Briefcase, MapPin, Clock } from "lucide-react";
import api from "../../lib/api";

export default function PositionsManager({ onChange }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", location: "Visakhapatnam", type: "Full-time", experience: "", order: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/site"); setList(data.positions || []); }
    catch { toast.error("Could not load positions"); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing("new"); setForm({ title: "", location: "Visakhapatnam", type: "Full-time", experience: "", order: list.length + 1 }); };
  const startEdit = (p) => { setEditing(p.id); setForm({ title: p.title || "", location: p.location || "", type: p.type || "Full-time", experience: p.experience || "", order: p.order || 0 }); };
  const cancel = () => { setEditing(null); setForm({ title: "", location: "Visakhapatnam", type: "Full-time", experience: "", order: 0 }); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: Number(form.order) || 0 };
      if (editing === "new") await api.post("/admin/positions", payload);
      else await api.patch(`/admin/positions/${editing}`, payload);
      toast.success("Saved");
      cancel(); load(); onChange && onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete position "${p.title}"?`)) return;
    try { await api.delete(`/admin/positions/${p.id}`); toast.success("Deleted"); load(); onChange && onChange(); }
    catch { toast.error("Delete failed"); }
  };

  if (editing !== null) {
    return (
      <div data-testid="position-editor" className="border border-[#1F3550] bg-[#0D1B2A] p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading font-bold text-xl text-[#F8FAFC]">{editing === "new" ? "New Position" : "Edit Position"}</h3>
          <button onClick={cancel} data-testid="position-cancel" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#F8FAFC]"><X className="h-4 w-4" /> Cancel</button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><Label>Job Title *</Label><Input data-testid="pos-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
          <div><Label>Location</Label><Input data-testid="pos-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <div><Label>Type (Full-time / Part-time / Contract)</Label><Input data-testid="pos-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} /></div>
          <div><Label>Experience required</Label><Input data-testid="pos-exp" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} required placeholder="3-5 years" /></div>
          <div><Label>Order</Label><Input type="number" data-testid="pos-order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} /></div>
          <div className="md:col-span-2"><button type="submit" disabled={saving} data-testid="pos-save" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-6 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910] disabled:opacity-50"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}</button></div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="positions-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading font-bold text-xl text-[#F8FAFC]">{list.length} open positions</h2>
        <button onClick={startCreate} data-testid="pos-new" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:bg-[#D68910]"><Plus className="h-4 w-4" /> New Position</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((p) => (
          <div key={p.id} data-testid={`pos-row-${p.id}`} className="border border-[#1F3550] bg-[#0D1B2A] p-5">
            <Briefcase className="h-5 w-5 text-[#F39C12]" />
            <h3 className="mt-3 font-heading font-bold text-[#F8FAFC]">{p.title}</h3>
            <div className="mt-3 space-y-1 font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8]">
              <div className="flex items-center gap-2"><MapPin className="h-3 w-3 text-[#F39C12]" />{p.location}</div>
              <div className="flex items-center gap-2"><Clock className="h-3 w-3 text-[#F39C12]" />{p.type} · {p.experience}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => startEdit(p)} data-testid={`pos-edit-${p.id}`} className="flex-1 border border-[#1F3550] hover:border-[#F39C12] text-[#F8FAFC] py-1.5 text-[10px] uppercase tracking-widest font-bold transition-colors">Edit</button>
              <button onClick={() => remove(p)} data-testid={`pos-delete-${p.id}`} className="border border-[#1F3550] hover:border-[#EF4444] hover:text-[#EF4444] text-[#94A3B8] px-3 transition-colors"><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full py-12 text-center text-[#94A3B8] border border-dashed border-[#1F3550]">No open positions.</div>}
      </div>
    </div>
  );
}

function Label({ children }) { return <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#94A3B8]">{children}</label>; }
function Input(props) { return <input {...props} className="mt-2 w-full bg-[#050A0F] border border-[#1F3550] text-[#F8FAFC] focus:border-[#F39C12] focus:ring-1 focus:ring-[#F39C12] px-4 py-2.5 outline-none" />; }
